import { RETRY_OR_SUPPORT_MESSAGE, TERMINOLOGY } from '@shared/i18n';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { CustomMessageBar, StatusLabel } from '@shared/components';
import {
  DETECTIONS_TABLE_COLUMN_IDS,
  DETECTIONS_TABLE_SIZING_PARAMS,
  REFERENCE_DETECTION_PREVIEW_TABLE_ID,
} from '../../constants';
import RefModeTabs from './RefModeTabs';
import { useGroupedRefs } from '../../hooks/useGroupedRefs.hook';
import {
  useCancelReferenceDetection,
  usePollReferenceDetection,
  useSubmitReferenceDetection,
} from '../../api/references.api';
import {
  EDetectionPollingStatus,
  EDetectionViewMode,
  IItemDefinition,
  TPollDetectionReturn,
} from '../../types/shared.types';
import { TItem, TResolvedItemsMap } from '@modules/items';
import DetectionsTable from '../shared/tables/DetectionsTable';
import PollingDialog from '../shared/dialogs/PollingDialog';
import useDetectionPreviewLifecycle from '../../hooks/useDetectionPreviewLifecycle.hook';
import useDetectionsColumns from '../../hooks/useDetectionsColumns.hook';
import PreviewActionsBar from '../shared/layout/PreviewActionsBar';
import PreviewHeader from '../shared/layout/PreviewHeader';
import useCheckedRows from '../../hooks/useCheckedRows.hook';
import { IReferenceDetectionRun, TReferenceDetectionItemType } from '../../types/references.types';

type TProps = {
  runId: string;
  setRunId: (id: string | undefined) => void;
  handleStart: () => void;
  setSelectedItems: Dispatch<SetStateAction<TItem[]>>;
};

const ReferenceDetectionPreviewContent = ({ runId, setRunId, setSelectedItems, handleStart }: TProps) => {
  const {
    pollQuery,
    run,
    resolvedItems,
    isAnalysisFailed,
    isPollingDialogOpen,
    setIsPollingDialogOpen,
    openPollingDialog,
    cancelMutation,
    handleCancelDetection,
  } = useDetectionPreviewLifecycle<IReferenceDetectionRun>({
    runId,
    usePoll: usePollReferenceDetection,
    useCancel: useCancelReferenceDetection,
    onCancelDone: () => {
      setRunId(undefined);
      setSelectedItems([]);
    },
    leaveGuardMessage: `Your ${TERMINOLOGY.reference.lowerPlural} aren’t inserted yet. Leaving will cancel the run. Proceed?`,
    summaryNavigateTo: (id) => `${id}/summary`,
  });

  return (
    <>
      <PollingDialog
        isOpen={isPollingDialogOpen}
        setIsOpen={setIsPollingDialogOpen}
        pollQuery={pollQuery}
        isAnalysisFailed={isAnalysisFailed}
        label={TERMINOLOGY.reference.lowerPlural}
      />

      <StatusLabel
        data={pollQuery.data && pollQuery.data.status === EDetectionPollingStatus.READY ? pollQuery.data : undefined}
        loadingMessage={`Loading ${TERMINOLOGY.reference.lowerPlural}...`}
        error={pollQuery.error}
        errorMessage={`Loading ${TERMINOLOGY.reference.lowerPlural} failed!`}
        render={(data) => (
          <PreviewContent
            run={data.run}
            resolvedItems={data.resolvedItems}
            isAnalysisFailed={isAnalysisFailed}
            cancelMutation={cancelMutation}
            handleCancelDetection={handleCancelDetection}
            handleStart={handleStart}
            openPollingDialog={openPollingDialog}
            targetDate={data.targetDate}
          />
        )}
      />
    </>
  );
};

type TInnerProps = {
  run: IReferenceDetectionRun;
  resolvedItems: TResolvedItemsMap;
  isAnalysisFailed: boolean;
  cancelMutation: ReturnType<typeof useCancelReferenceDetection>;
  handleCancelDetection: () => void;
  handleStart: () => void;
  openPollingDialog: () => void;
  targetDate: string;
};

const PreviewContent = ({
  run,
  resolvedItems,
  isAnalysisFailed,
  cancelMutation,
  handleCancelDetection,
  handleStart,
  openPollingDialog,
  targetDate,
}: TInnerProps) => {
  const { selectedTab, setSelectedTab, groupedRefs, groupedByType } = useGroupedRefs(
    run.detectedReferences,
    run.detectedSelfReferences
  );

  const allRefs = [...run.detectedReferences, ...run.detectedSelfReferences];
  const { checkedRowIds, setCheckedRowIds } = useCheckedRows(allRefs);
  const [chosenTargetItemMap, setChosenTargetItemMap] = useState<
    Map<string, IItemDefinition<TReferenceDetectionItemType>>
  >(() => new Map(run.detectedReferences.map((r) => [r.id, r.chosenTargetItem ?? r.targetItems[0]])));

  const submitMutation = useSubmitReferenceDetection(run._id);
  const handleInsertReferences = () => {
    submitMutation.mutate(
      {
        chosenReferences: run.detectedReferences
          .filter((ref) => checkedRowIds.has(ref.id))
          .map((ref) => ({
            id: ref.id,
            chosenTargetItem: {
              itemId: chosenTargetItemMap.get(ref.id)?.item as string,
              itemType: chosenTargetItemMap.get(ref.id)?.itemType as TReferenceDetectionItemType,
            },
          })),
        chosenSelfReferences: run.detectedSelfReferences
          .filter((ref) => checkedRowIds.has(ref.id))
          .map((ref) => ref.id),
      },
      { onSuccess: openPollingDialog }
    );
  };

  const columnSizing = useDetectionsColumns<{ id: string }>(
    REFERENCE_DETECTION_PREVIEW_TABLE_ID,
    DETECTIONS_TABLE_COLUMN_IDS,
    DETECTIONS_TABLE_SIZING_PARAMS
  );

  return (
    <div className="flex flex-col space-y-6">
      <PreviewHeader
        left={<RefModeTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} groupedByType={groupedByType} />}
        rightDisabled={submitMutation.isPending || cancelMutation.isPending}
        onRestart={handleStart}
        targetDate={targetDate}
      />

      <div className="flex flex-col space-y-4">
        {isAnalysisFailed ? (
          <CustomMessageBar
            intent="error"
            title={`Error analyzing ${TERMINOLOGY.reference.lowerPlural}.`}
            body={RETRY_OR_SUPPORT_MESSAGE}
          />
        ) : Object.entries(groupedRefs).length > 0 ? (
          Object.entries(groupedRefs).map(([sourceDocId, detectedRefs]) => (
            <DetectionsTable
              key={sourceDocId}
              variant="references"
              columnSizing={columnSizing}
              sourceId={sourceDocId}
              detectedElements={detectedRefs}
              checkedRowIds={checkedRowIds}
              setCheckedRowIds={setCheckedRowIds}
              resolvedItems={resolvedItems}
              chosenTargetItemMap={chosenTargetItemMap}
              setChosenTargetItemMap={setChosenTargetItemMap}
            />
          ))
        ) : (
          <CustomMessageBar
            intent="info"
            title={`No ${TERMINOLOGY.reference.lowerPlural} found.`}
            body="Please make sure you have selected the correct folders and run the detection again."
          />
        )}
      </div>

      <PreviewActionsBar
        onCancel={handleCancelDetection}
        onInsert={handleInsertReferences}
        cancelLoading={cancelMutation.isPending}
        insertLoading={submitMutation.isPending}
        insertDisabled={checkedRowIds.size === 0}
        count={checkedRowIds.size}
        nounSingular={TERMINOLOGY.reference.lower}
        nounPlural={TERMINOLOGY.reference.lowerPlural}
        targetDate={targetDate}
      />
    </div>
  );
};

export default ReferenceDetectionPreviewContent;
